import React from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";
import { useFormikContext } from "formik";
import LabeledSelect from "../../Widgets/LabeledSelect";
import LabeledDatePicker from "../../Widgets/LabeledDatePicker";
import LabeledTextArea from "../../Widgets/LabeledTextArea";
import {
  IQ_TEST,
  CARS2_TYPE,
  ASD_SEVERITY,
  MEDICAL_DIAGNOSES,
  ADDITIONAL_DIAGNOSES,
} from "components/ClientDetails/Sections/Profile/DiagnosticProfile/selectValues";
import { Diagnosis, DiagnosticFormikValues } from "./interface";

const AUTISM_DIAGNOSES_CODE = "F84.0";

interface EditFieldsProps {
  listOfDiagnosis: Diagnosis[];
}

export const EditFields: React.FC<EditFieldsProps> = ({ listOfDiagnosis }) => {
  const { touched, errors, handleBlur, handleChange, setFieldValue, setFieldTouched, values } =
    useFormikContext<DiagnosticFormikValues>();
  const { diagnosticProfile } = values;

  const handleMultipleSelectChange = (event: any) => {
    const {
      target: { value, name },
    } = event;

    const updatedDiagnoses = typeof value === "string" ? value.split(",") : value;
    setFieldValue(name, updatedDiagnoses);
  };

  const findDiagnosisById = (diagnosisId: number | string) => {
    return listOfDiagnosis.find((diagnosis) => diagnosis.id === diagnosisId);
  };

  const handleTouch = (name: string) => {
    setFieldTouched(name, true);
  };

  return (
    <Grid container spacing={7}>
      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="Diagnosis"
          name="diagnosticProfile.diagnosisId"
          error={
            touched?.diagnosticProfile?.diagnosisId && !!errors?.diagnosticProfile?.diagnosisId
          }
          helperText={
            touched?.diagnosticProfile?.diagnosisId && errors?.diagnosticProfile?.diagnosisId
          }
          onBlur={handleBlur}
          onChange={handleChange}
          value={diagnosticProfile.diagnosisId}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {listOfDiagnosis.map((diagnosis) => (
            <MenuItem key={diagnosis.code} value={diagnosis.id}>
              {diagnosis.code} - {diagnosis.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <LabeledDatePicker
          id="diagnosis-date"
          label="Diagnosis date (primary)"
          value={diagnosticProfile?.diagnosisDate}
          onBlur={handleBlur}
          onChange={(value) => setFieldValue("diagnosticProfile.diagnosisDate", value, true)}
          disableFuture
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="additional-diagnoses-label">
            Additional diagnoses (mental health and neurodevelopmental)
          </InputLabel>
          <Select
            multiple
            id="additional-diagnoses"
            name="diagnosticProfile.additionalDiagnoses"
            labelId="additional-diagnoses-label"
            onClose={() => handleTouch("diagnosticProfile.additionalDiagnoses")}
            onChange={handleMultipleSelectChange}
            value={diagnosticProfile.additionalDiagnoses}
            renderValue={(selected) => selected?.join(", ")}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {ADDITIONAL_DIAGNOSES.map((diagnosis) => (
              <MenuItem key={diagnosis.code} value={diagnosis.code}>
                <Checkbox
                  checked={diagnosticProfile.additionalDiagnoses?.includes(diagnosis.code)}
                />
                <ListItemText primary={diagnosis.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="medical-diagnoses-label">Medical diagnoses</InputLabel>
          <Select
            multiple
            labelId="medical-diagnoses-label"
            id="medical-diagnoses"
            name="diagnosticProfile.medicalDiagnoses"
            onClose={() => handleTouch("diagnosticProfile.medicalDiagnoses")}
            onChange={handleMultipleSelectChange}
            value={diagnosticProfile.medicalDiagnoses}
            renderValue={(selected) => selected?.join(", ")}
            required
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {MEDICAL_DIAGNOSES.map((diagnosis) => (
              <MenuItem key={diagnosis.value} value={diagnosis.value}>
                <Checkbox checked={diagnosticProfile.medicalDiagnoses?.includes(diagnosis.value)} />
                <ListItemText primary={diagnosis.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {findDiagnosisById(diagnosticProfile.diagnosisId)?.code === AUTISM_DIAGNOSES_CODE && (
        <>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="ASD severity - social communication"
              name="diagnosticProfile.asdSeveritySocial"
              onBlur={handleBlur}
              onChange={handleChange}
              value={diagnosticProfile.asdSeveritySocial}
            >
              <MenuItem disabled value={""}>
                Select
              </MenuItem>
              {ASD_SEVERITY.map((severity) => (
                <MenuItem key={severity.value} value={severity.value}>
                  {severity.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="ASD severity - restricted, repetitive behaviors"
              name="diagnosticProfile.asdSeverityRestricted"
              onBlur={handleBlur}
              onChange={handleChange}
              value={diagnosticProfile.asdSeverityRestricted}
            >
              <MenuItem disabled value={""}>
                Select
              </MenuItem>
              {ASD_SEVERITY.map((severity) => (
                <MenuItem key={severity.value} value={severity.value}>
                  {severity.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="IQ test on file"
          name="diagnosticProfile.iqTest"
          onBlur={handleBlur}
          onChange={handleChange}
          value={diagnosticProfile.iqTest}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {IQ_TEST.map((iqTest) => (
            <MenuItem key={iqTest.value} value={iqTest.value}>
              {iqTest.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Full scale IQ score"
          name="diagnosticProfile.iqScore"
          size="small"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.iqScore}
          error={touched?.diagnosticProfile?.iqScore && !!errors?.diagnosticProfile?.iqScore}
          helperText={touched?.diagnosticProfile?.iqScore && errors?.diagnosticProfile?.iqScore}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          select
          fullWidth
          label="CARS-2 type"
          name="diagnosticProfile.cars2Type"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.cars2Type}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {CARS2_TYPE.map((cars2Type) => (
            <MenuItem key={cars2Type.value} value={cars2Type.value}>
              {cars2Type.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="CARS-2 t-score"
          name="diagnosticProfile.cars2TScore"
          size="small"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.cars2TScore}
          error={
            touched?.diagnosticProfile?.cars2TScore && !!errors?.diagnosticProfile?.cars2TScore
          }
          helperText={
            touched?.diagnosticProfile?.cars2TScore && errors?.diagnosticProfile?.cars2TScore
          }
        />
      </Grid>

      <LabeledSelect
        label="Evaluation for Comorbid Diagnoses"
        name="diagnosticProfile.evaluationForComorbidDiagnoses"
        value={diagnosticProfile.evaluationForComorbidDiagnoses}
        onChange={handleChange}
        onBlur={handleBlur}
        options={[
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
          { label: "N/A", value: "N/A" },
        ]}
      />

      <LabeledTextArea
        multiline
        label="Other symptoms and functional impairments observed"
        name="diagnosticProfile.otherSymptomsObserved"
        value={diagnosticProfile.otherSymptomsObserved}
        onChange={handleChange}
      />

      <LabeledTextArea
        multiline
        label="Persistent Deficits in Social Communication and Interaction"
        name="diagnosticProfile.persistentDeficitsSocialCommunication"
        value={diagnosticProfile.persistentDeficitsSocialCommunication}
        onChange={handleChange}
      />

      <LabeledTextArea
        multiline
        label="Restrictive, Repetitive Patterns of Behavior or Activity"
        name="diagnosticProfile.restrictiveRepetitivePatterns"
        value={diagnosticProfile.restrictiveRepetitivePatterns}
        onChange={handleChange}
      />

    </Grid>
  );
};

export default EditFields;
